// YNX BUSINESS HUB — multi-module nav: Agenda, Tasks, Devis/Factures,
// Clients, Annuaire interne, Studio, Paramètres, Support.
// ───────────────────────────────────────────────────────────────
import React, { useState } from 'react';
import {
  CalendarDays, KanbanSquare, ReceiptText, Users, UserCog,
  PhoneCall, Image as ImageIcon, Settings, LifeBuoy,
  MessageSquare, LogOut, Copy, CreditCard, Eye, Video,
} from 'lucide-react';
import { clearLocalWorkspace } from '../lib/biz';
import { logout as authLogout } from '../lib/auth';
import { DemoBanner, isDemoWorkspace } from './DemoBanner';
import './demo.css';

import { AgendaModule } from './modules/AgendaModule';
import { TasksModule } from './modules/TasksModule';
import { InvoicesModule } from './modules/InvoicesModule';
import { ClientsModule } from './modules/ClientsModule';
import { DirectoryModule } from './modules/DirectoryModule';
import { SettingsModule } from './modules/SettingsModule';
import { SupportModule } from './modules/SupportModule';
import { BillingModule } from './modules/BillingModule';
import { MembersModule } from './modules/MembersModule';
import { MeetingsModule } from './modules/MeetingsModule';
import './billing.css';

const TABS = [
  { id: 'chat',      label: 'Assistant IA',     icon: MessageSquare },
  { id: 'agenda',    label: 'Agenda',           icon: CalendarDays },
  { id: 'meetings',  label: 'Réunions',         icon: Video },
  { id: 'tasks',     label: 'Tâches',           icon: KanbanSquare },
  { id: 'invoices',  label: 'Devis & Factures', icon: ReceiptText },
  { id: 'clients',   label: 'Clients',          icon: Users },
  { id: 'directory', label: 'Annuaire interne', icon: PhoneCall },
  { id: 'studio',    label: 'Studio Visuel',    icon: ImageIcon },
  { id: 'members',   label: 'Équipe',           icon: UserCog },
  { id: 'billing',   label: 'Abonnement',       icon: CreditCard },
  { id: 'settings',  label: 'Paramètres',       icon: Settings },
  { id: 'support',   label: 'Support',          icon: LifeBuoy },
];

export const BusinessHub = ({ workspace, setWorkspace, currentUser, setCurrentUser, openStudio, openChat }) => {
  // Auto-switch to billing tab if we're returning from Stripe Checkout
  const initialTab = (() => {
    try {
      const p = new URLSearchParams(window.location.search);
      if (p.get('billing')) return 'billing';
    } catch (err) { console.debug('YNX hub: query parse failed', err); }
    return 'agenda';
  })();
  const [tab, setTab] = useState(initialTab);

  const copyCode = async () => {
    try { await navigator.clipboard.writeText(workspace.access_code); }
    catch (err) { console.warn('YNX: clipboard copy failed', err); }
  };

  const leaveWorkspace = () => {
    if (!window.confirm('Quitter cet espace entreprise sur ce navigateur ?')) return;
    clearLocalWorkspace();
    setWorkspace(null);
  };

  /** Visitor preview — see the site as a brand-new prospect would,
   *  WITHOUT losing your own workspace. We stash the current workspace_id
   *  in sessionStorage and reload at the landing. A floating "Restore"
   *  button lets you come back to your space in one click. */
  const previewAsVisitor = () => {
    try {
      const wsId = localStorage.getItem('ynx_workspace_id') || workspace?.id || '';
      if (wsId) sessionStorage.setItem('ynx_preview_restore_ws', wsId);
      sessionStorage.setItem('ynx_preview_restore_name', workspace?.name || '');
      clearLocalWorkspace();
      window.location.href = '/?preview=visitor';
    } catch (e) {
      console.warn('YNX preview-visitor failed', e);
      // Fallback: just clear + go home
      clearLocalWorkspace();
      window.location.href = '/';
    }
  };

  const handleLogout = async () => {
    if (!window.confirm('Se déconnecter de votre compte Google ?')) return;
    await authLogout();
    if (setCurrentUser) setCurrentUser(null);
    clearLocalWorkspace();
    setWorkspace(null);
  };

  const isDemo = isDemoWorkspace(workspace);
  const handleSubscribe = () => {
    // Leave demo and go to onboarding "create" mode
    clearLocalWorkspace();
    try { window.location.href = '/?onboard=1'; }
    catch (err) { console.warn('YNX hub: navigation to /?onboard=1 failed', err); }
  };

  return (
    <div className={`biz-hub-wrapper ${isDemo ? 'ynx-demo-mode' : ''}`}>
      <DemoBanner
        workspace={workspace}
        onLeave={() => { clearLocalWorkspace(); setWorkspace(null); }}
        onSubscribe={handleSubscribe}
      />
      <div className='biz-hub'>
      <aside className='biz-sidebar'>
        <div className='biz-side-brand'>
          <div className='biz-side-name'>{workspace?.name || 'Entreprise'}</div>
          <div className='biz-side-code' title="Code d'accès — partagez-le avec vos collaborateurs">
            <span>{workspace?.access_code}</span>
            <button className='biz-icon-btn' onClick={copyCode} title='Copier le code'>
              <Copy size={12} />
            </button>
          </div>
        </div>
        <nav className='biz-nav'>
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                className={`biz-nav-item ${active ? 'active' : ''}`}
                onClick={() => {
                  if (t.id === 'chat') { openChat?.(); return; }
                  if (t.id === 'studio') { openStudio?.(); return; }
                  setTab(t.id);
                }}
                data-testid={`biz-nav-${t.id}`}
              >
                <Icon size={16} /> <span>{t.label}</span>
              </button>
            );
          })}
        </nav>
        <button
          className='biz-preview-btn'
          onClick={previewAsVisitor}
          data-testid='biz-preview-visitor-btn'
          title='Voir votre site comme un nouveau visiteur, sans perdre vos données'
        >
          <Eye size={13} /> Aperçu visiteur
        </button>
        <button className='biz-leave-btn' onClick={leaveWorkspace} data-testid='biz-leave-btn'>
          <LogOut size={13} /> Quitter l'espace
        </button>
        {currentUser && (
          <button
            className='biz-leave-btn'
            onClick={handleLogout}
            style={{ marginTop: 6, opacity: 0.7 }}
            data-testid='biz-logout-btn'
            title={`Connecté en tant que ${currentUser.email}`}
          >
            <LogOut size={13} /> Se déconnecter ({currentUser.email.split('@')[0]})
          </button>
        )}
        <div className='biz-side-footer'>
          YNX <b>by Carl TACITA</b><br />
          <span style={{ opacity: 0.6 }}>v1.0 · Emergent</span>
        </div>
      </aside>

      <main className='biz-main'>
        {tab === 'agenda'    && <AgendaModule />}
        {tab === 'meetings'  && <MeetingsModule workspace={workspace} currentUser={currentUser} />}
        {tab === 'tasks'     && <TasksModule />}
        {tab === 'invoices'  && <InvoicesModule workspace={workspace} />}
        {tab === 'clients'   && <ClientsModule />}
        {tab === 'directory' && <DirectoryModule />}
        {tab === 'members'   && <MembersModule workspace={workspace} currentUser={currentUser} />}
        {tab === 'billing'   && <BillingModule workspace={workspace} setWorkspace={setWorkspace} />}
        {tab === 'settings'  && <SettingsModule workspace={workspace} setWorkspace={setWorkspace} />}
        {tab === 'support'   && <SupportModule workspace={workspace} />}
      </main>
      </div>
    </div>
  );
};
